import { useEffect, useState } from "react";
import {
    BarChart3Icon,
    TrendingUpIcon,
    UsersIcon,
    GaugeIcon,
    CheckCircle2Icon,
    StarIcon,
    CalendarIcon,
} from "lucide-react";
import TrainingAuthLayout from "@container/layout/training-auth";
import { Card, CardContent } from "@component/card";
import { StatTile } from "@component/training/stat-tile";
import {
    getReportsData,
    getReportsSummary,
    getReportsCompletionTrend,
    pickPersonaName,
} from "@service/training";
import type { ReportsData, ReportsSummary, ReportsCompletionTrend } from "@service/training";
import { TrainingErrorCard } from "@component/training/training-async-state";
import { catchErrorLog } from "@util/sentry";
import { cn } from "@util/tailwind";

interface TrainingReportsPageProps {
    // When true, the component renders without TrainingAuthLayout wrapping
    // and without the page-level h1 header — used when embedded inside the
    // Performance page's "Team Performance" sub-tab.
    embedded?: boolean;
}

export default function TrainingReportsPage({ embedded = false }: TrainingReportsPageProps = {}) {
    const [data, setData] = useState<ReportsData | null>(null);
    const [summary, setSummary] = useState<ReportsSummary | null>(null);
    const [summaryLoading, setSummaryLoading] = useState(true);
    const [trend, setTrend] = useState<ReportsCompletionTrend | null>(null);
    const [trendLoading, setTrendLoading] = useState(true);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState<Error | null>(null);
    const [reloadTick, setReloadTick] = useState(0);

    useEffect(() => {
        let cancelled = false;
        setLoading(true);
        setError(null);
        getReportsData()
            .then((d) => {
                if (cancelled) return;
                setData(d);
                setLoading(false);
            })
            .catch((err: unknown) => {
                if (cancelled) return;
                const e = err instanceof Error ? err : new Error(String(err));
                catchErrorLog(`[training] reports load failed: ${e.message}`);
                setError(e);
                setLoading(false);
            });
        return () => {
            cancelled = true;
        };
    }, [reloadTick]);

    // Header cards are sourced from the dedicated summary endpoint (not derived
    // from the charts data). They render a skeleton while this is in flight.
    useEffect(() => {
        let cancelled = false;
        setSummaryLoading(true);
        getReportsSummary()
            .then((s) => { if (!cancelled) setSummary(s); })
            .catch(() => {})
            .finally(() => { if (!cancelled) setSummaryLoading(false); });
        return () => { cancelled = true; };
    }, [reloadTick]);

    // Completion trend chart comes from its own endpoint (last 30 days).
    useEffect(() => {
        let cancelled = false;
        setTrendLoading(true);
        getReportsCompletionTrend(30)
            .then((t) => { if (!cancelled) setTrend(t); })
            .catch(() => {})
            .finally(() => { if (!cancelled) setTrendLoading(false); });
        return () => { cancelled = true; };
    }, [reloadTick]);

    const body = (
        <div>
            {!embedded && (
                <div className="mb-6">
                    <h1 className="text-2xl font-semibold text-gray-900">Reports</h1>
                    <p className="text-sm text-gray-600 mt-1">
                        How your team is performing across all training activity.
                    </p>
                </div>
            )}

                {error ? (
                    <TrainingErrorCard
                        title="We couldn't load reports."
                        message="The error has been reported. Try again or refresh in a minute."
                        onRetry={() => setReloadTick((t) => t + 1)}
                    />
                ) : loading || !data ? (
                    <SkeletonReports />
                ) : (
                    <>
                        <div className="grid grid-cols-1 sm:grid-cols-3 gap-4 mb-6">
                            <StatTile
                                label="Total completed sessions"
                                value={summary?.total_completed_sessions ?? "—"}
                                loading={summaryLoading}
                                icon={CheckCircle2Icon}
                                tooltip="Total number of training sessions completed across your team."
                            />
                            <StatTile
                                label="Overall avg score"
                                value={`${summary?.overall_avg_score != null ? Math.round(summary.overall_avg_score) : "—"}/100`}
                                loading={summaryLoading}
                                icon={StarIcon}
                                tooltip="Average score across all completed training sessions, out of 100."
                            />
                            <StatTile
                                label="Sessions in last 7 days"
                                value={summary?.sessions_in_last_7days ?? "—"}
                                loading={summaryLoading}
                                icon={CalendarIcon}
                                tooltip="Number of training sessions completed in the last 7 days."
                            />
                        </div>

                        <div className="grid grid-cols-1 lg:grid-cols-2 divide-y lg:divide-y-0 lg:divide-x divide-gray-100 border border-gray-100 rounded-lg mb-4 overflow-hidden">
                            <div className="p-5">
                                <SectionHeader
                                    icon={<BarChart3Icon className="w-4 h-4 text-blue-600" />}
                                    title="Score distribution"
                                    subtitle="How many completed sessions fall into each score band, out of 100."
                                />
                                <ScoreHistogram buckets={data.score_buckets} />
                            </div>

                            <div className="p-5">
                                <SectionHeader
                                    icon={<TrendingUpIcon className="w-4 h-4 text-blue-600" />}
                                    title="Completion trend"
                                    subtitle="Number of training sessions completed on each day over the last 30 days."
                                />
                                {trendLoading ? (
                                    <CompletionTrendSkeleton />
                                ) : trend && trend.points.length > 0 ? (
                                    <CompletionTrendChart trend={trend} />
                                ) : (
                                    <div className="text-sm text-gray-500 italic py-6 text-center">
                                        No completion data yet.
                                    </div>
                                )}
                            </div>
                        </div>

                        <div className="grid grid-cols-1 lg:grid-cols-2 divide-y lg:divide-y-0 lg:divide-x divide-gray-100 border border-gray-100 rounded-lg mb-6 overflow-hidden">
                            <div className="p-5">
                                <SectionHeader
                                    icon={<GaugeIcon className="w-4 h-4 text-blue-600" />}
                                    title="Persona effectiveness"
                                    subtitle="Average score employees achieve against each persona. A higher score means employees handle that persona's scenario more successfully."
                                />
                                <PersonaEffectivenessChart
                                    data={data.persona_effectiveness}
                                />
                            </div>

                            <div className="p-5">
                                <SectionHeader
                                    icon={<UsersIcon className="w-4 h-4 text-blue-600" />}
                                    title="Department leaderboard"
                                    subtitle="Average score for each department, ranked from highest to lowest."
                                />
                                <DepartmentLeaderboard data={data.department_leaderboard} />
                            </div>
                        </div>
                    </>
                )}
        </div>
    );

    return embedded ? body : <TrainingAuthLayout>{body}</TrainingAuthLayout>;
}

function SectionHeader({
    icon,
    title,
    subtitle,
}: {
    icon: React.ReactNode;
    title: string;
    subtitle?: string;
}) {
    return (
        <div className="mb-4">
            <div className="flex items-center gap-2 mb-0.5">
                {icon}
                <h2 className="text-base font-semibold text-gray-800">{title}</h2>
            </div>
            {subtitle && <p className="text-xs text-gray-500">{subtitle}</p>}
        </div>
    );
}

function ScoreHistogram({
    buckets,
}: {
    buckets: ReportsData["score_buckets"];
}) {
    // Internal viewBox is landscape so the SVG stretches to fill the card
    // width without becoming awkwardly tall on wide screens. preserveAspect
    // (default xMidYMid meet) keeps bar proportions sane.
    const max = Math.max(1, ...buckets.map((b) => b.count));
    const width = 800;
    const height = 200;
    const padX = 40;
    const padY = 28;
    const innerW = width - padX * 2;
    const innerH = height - padY * 2;
    const barW = innerW / buckets.length - 20;

    return (
        <div>
            <svg
                width="100%"
                viewBox={`0 0 ${width} ${height}`}
                className="text-xs block"
            >
                {/* Y axis ticks */}
                {[0, 0.5, 1].map((t, i) => {
                    const y = padY + innerH - innerH * t;
                    const v = Math.round(max * t);
                    return (
                        <g key={i}>
                            <line
                                x1={padX}
                                x2={padX + innerW}
                                y1={y}
                                y2={y}
                                stroke="#f1f5f9"
                                strokeWidth="1"
                            />
                            <text x={padX - 6} y={y + 3} textAnchor="end" fill="#94a3b8">
                                {v}
                            </text>
                        </g>
                    );
                })}

                {buckets.map((b, i) => {
                    const x = padX + i * (innerW / buckets.length) + 6;
                    const h = (b.count / max) * innerH;
                    const y = padY + innerH - h;
                    // Single brand color across all buckets.
                    const color = "#3b82f6";
                    return (
                        <g key={b.range}>
                            <rect
                                x={x}
                                y={y}
                                width={barW}
                                height={h}
                                fill={color}
                                rx="3"
                            />
                            {b.count > 0 && (
                                <text
                                    x={x + barW / 2}
                                    y={y - 4}
                                    textAnchor="middle"
                                    fill="#475569"
                                    fontWeight="600"
                                >
                                    {b.count}
                                </text>
                            )}
                            <text
                                x={x + barW / 2}
                                y={padY + innerH + 14}
                                textAnchor="middle"
                                fill="#64748b"
                            >
                                {b.range}
                            </text>
                        </g>
                    );
                })}
            </svg>
        </div>
    );
}

// Format a YYYY-MM-DD day into a short axis label (e.g. "4 May"). Parsed as a
// UTC date and formatted with timeZone: "UTC" so the day never shifts.
function formatTrendDay(date?: string): string {
    if (!date) return "";
    const [y, m, d] = date.split("-").map(Number);
    if (!y || !m || !d) return date;
    return new Date(Date.UTC(y, m - 1, d)).toLocaleDateString("en-GB", {
        day: "numeric",
        month: "short",
        year: "numeric",
        timeZone: "UTC",
    });
}

function CompletionTrendChart({ trend }: { trend: ReportsCompletionTrend }) {
    // Compact sparkline footprint (was 480×180) — sparse data didn't justify
    // the larger chart. Drops Y-axis ticks and trims X labels to endpoints
    // since the total is shown as a number below.
    const points = trend.points;
    const max = Math.max(1, ...points.map((p) => p.completed));
    const width = 480;
    const height = 96;
    const padX = 8;
    const padY = 10;
    const innerW = width - padX * 2;
    const innerH = height - padY * 2;
    const xs = points.map((_, i) =>
        points.length > 1 ? padX + (i / (points.length - 1)) * innerW : padX + innerW / 2,
    );
    const ys = points.map(
        (p) => padY + innerH - (p.completed / max) * innerH
    );
    const polyline = xs.map((x, i) => `${x},${ys[i]}`).join(" ");
    const areaPath = `M ${xs[0]},${padY + innerH} L ${polyline.split(" ").join(" L ")} L ${xs[xs.length - 1]},${padY + innerH} Z`;

    return (
        <div>
            <div className="flex items-baseline justify-between mb-1.5">
                <div className="text-2xl font-semibold text-gray-900 tabular-nums">{trend.total_completed}</div>
                <div className="text-xs text-gray-500">
                    Busiest day: {trend.peak_per_day} session{trend.peak_per_day === 1 ? "" : "s"}
                </div>
            </div>
            <svg width="100%" viewBox={`0 0 ${width} ${height}`} preserveAspectRatio="none" className="block">
                <defs>
                    <linearGradient id="trendFill" x1="0" y1="0" x2="0" y2="1">
                        <stop offset="0%" stopColor="#10b981" stopOpacity="0.25" />
                        <stop offset="100%" stopColor="#10b981" stopOpacity="0" />
                    </linearGradient>
                </defs>
                <path d={areaPath} fill="url(#trendFill)" />
                <polyline
                    points={polyline}
                    fill="none"
                    stroke="#10b981"
                    strokeWidth="2"
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    vectorEffect="non-scaling-stroke"
                />
            </svg>
            <div className="flex justify-between text-[10px] text-gray-400 mt-1 px-1">
                <span>{formatTrendDay(points[0]?.date)}</span>
                <span>{formatTrendDay(points[points.length - 1]?.date)}</span>
            </div>
        </div>
    );
}

function CompletionTrendSkeleton() {
    return (
        <div aria-busy="true">
            <div className="flex items-baseline justify-between mb-1.5">
                <div className="h-7 w-10 bg-gray-100 rounded animate-pulse" />
                <div className="h-3 w-16 bg-gray-100 rounded animate-pulse" />
            </div>
            <div className="h-24 w-full bg-gray-50 rounded animate-pulse" />
            <div className="flex justify-between mt-1 px-1">
                <div className="h-2.5 w-10 bg-gray-100 rounded animate-pulse" />
                <div className="h-2.5 w-10 bg-gray-100 rounded animate-pulse" />
            </div>
        </div>
    );
}

// BE sometimes returns the persona UUID in `persona_name` when the lookup
// fails (the FE used to show e.g. "81627bd2-a9e4-449e-8743-b574d33f9cd3").
// Detect UUID-shape names and fall back to the FE's curated persona label
// table, or a friendly placeholder.
const UUID_RE = /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i;
function displayPersonaName(p: { persona_id: string; persona_name?: string | null }): string {
    const name = (p.persona_name ?? "").trim();
    if (name && !UUID_RE.test(name)) return name;
    const fromTable = pickPersonaName(p.persona_id);
    if (fromTable && !UUID_RE.test(fromTable)) return fromTable;
    return "Unknown persona";
}

function PersonaEffectivenessChart({
    data,
}: {
    data: ReportsData["persona_effectiveness"];
}) {
    if (data.length === 0) {
        return (
            <div className="text-sm text-gray-500 italic py-6 text-center">
                No completed sessions yet.
            </div>
        );
    }
    return (
        <ol className="space-y-2.5">
            {data.map((p, i) => (
                <li key={p.persona_id} className="flex items-center justify-between gap-3 py-0.5">
                    <div className="flex items-center gap-3 min-w-0">
                        <div className={cn(
                            "w-7 h-7 rounded-full flex items-center justify-center text-xs font-bold flex-shrink-0",
                            i === 0 ? "bg-blue-600 text-white"
                            : i === 1 ? "bg-gray-100 text-gray-600"
                            : "bg-gray-50 text-gray-500",
                        )}>
                            {i + 1}
                        </div>
                        <span className="text-sm text-gray-700 truncate">{displayPersonaName(p)}</span>
                    </div>
                    <div className="text-sm font-semibold text-gray-900 tabular-nums whitespace-nowrap">
                        {Math.round(p.avg_score)}<span className="text-gray-400">/100</span>
                        <span className="text-gray-400 font-normal ml-1.5">· {p.sessions_count} session{p.sessions_count === 1 ? "" : "s"}</span>
                    </div>
                </li>
            ))}
        </ol>
    );
}

function DepartmentLeaderboard({
    data,
}: {
    data: ReportsData["department_leaderboard"];
}) {
    if (data.length === 0) {
        return (
            <div className="text-sm text-gray-500 italic py-6 text-center">
                No department data yet.
            </div>
        );
    }
    return (
        <ol className="space-y-2.5">
            {data.map((d, i) => (
                <li
                    key={d.department}
                    className="flex items-center justify-between gap-3 py-1"
                >
                    <div className="flex items-center gap-3 min-w-0">
                        <div
                            className={cn(
                                "w-7 h-7 rounded-full flex items-center justify-center text-xs font-bold flex-shrink-0",
                                i === 0
                                    ? "bg-blue-600 text-white"
                                    : i === 1
                                    ? "bg-gray-100 text-gray-700"
                                    : "bg-gray-50 text-gray-600"
                            )}
                        >
                            {i + 1}
                        </div>
                        <div className="min-w-0">
                            <div className="text-sm font-medium text-gray-900 truncate">
                                {d.department}
                            </div>
                            <div className="text-xs text-gray-500">
                                {d.people_count} people · {d.completed_count} sessions
                            </div>
                        </div>
                    </div>
                    <div className="text-sm font-semibold text-gray-900 tabular-nums">
                        {Math.round(d.avg_score)}
                        <span className="text-gray-400">/100</span>
                    </div>
                </li>
            ))}
        </ol>
    );
}

function SkeletonReports() {
    return (
        <>
            <div className="grid grid-cols-1 sm:grid-cols-3 gap-4 mb-6">
                {[0, 1, 2].map((i) => (
                    <Card key={i}>
                        <CardContent className="p-4">
                            <div className="h-3 w-24 bg-gray-100 rounded animate-pulse mb-2" />
                            <div className="h-7 w-16 bg-gray-100 rounded animate-pulse" />
                        </CardContent>
                    </Card>
                ))}
            </div>
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
                {[0, 1, 2, 3].map((i) => (
                    <Card key={i}>
                        <CardContent className="p-5">
                            <div className="h-3 w-32 bg-gray-100 rounded animate-pulse mb-4" />
                            <div className="h-40 bg-gray-50 rounded animate-pulse" />
                        </CardContent>
                    </Card>
                ))}
            </div>
        </>
    );
}
